import { Panel } from "../components/Bits";
import { HarvestShell, useHarvestHeader } from "./HarvestShell";
import { addDays, formatLong, toDate, toIso } from "../../lib/harvestDates";

const START = "2026-11-02";
const END = "2027-01-29";
const DAY_W = 9;
const LANE_H = 34;
const LEFT = 186;
const TOP = 54;

const OWNER_TONES = {
  auto: { fill: "#7C9A74", label: "Automated (Apollo)" },
  approval: { fill: "#C8A24A", label: "Approval gate" },
  kyle: { fill: "#C96E4A", label: "Kyle manual" },
};

const LANES = [
  {
    name: "Tier 1 · sequence batch 1",
    bars: [{ start: "2026-11-16", end: "2026-12-06", owner: "auto", label: "Batch 1 running" }],
  },
  {
    name: "Tier 1 · batch 2 enrolls",
    bars: [{ start: "2026-11-23", end: "2026-12-04", owner: "auto", label: "Batch 2 enrolls" }],
  },
  {
    name: "Tier 1 · boxes",
    bars: [
      { start: "2026-11-16", end: "2026-12-04", owner: "kyle", label: "Fall wave" },
      { start: "2027-01-05", end: "2027-01-15", owner: "kyle", label: "Winter wave" },
    ],
  },
  {
    name: "Tier 2 · Field Note",
    bars: [
      { start: "2026-11-30", end: "2026-12-11", owner: "approval", label: "Batch 1" },
      { start: "2027-01-11", end: "2027-01-22", owner: "approval", label: "Batch 2" },
    ],
  },
  {
    name: "Tier 3 · video + postcard",
    bars: [{ start: "2027-01-04", end: "2027-01-22", owner: "approval", label: "Tier 3 wave" }],
  },
  {
    name: "Partners · kits",
    bars: [{ start: "2026-11-09", end: "2026-12-04", owner: "kyle", label: "Kit hand-off" }],
    dots: [{ date: "2027-01-21", owner: "kyle", label: "Harvest Table breakfast" }],
  },
  {
    name: "Chamber · events",
    dots: [
      { date: "2026-11-18", owner: "kyle", label: "Chamber event" },
      { date: "2026-12-01", owner: "kyle", label: "Chamber event" },
      { date: "2026-12-10", owner: "kyle", label: "Chamber event" },
      { date: "2026-12-16", owner: "kyle", label: "Chamber event" },
    ],
  },
];

const PHASE_TONES = { Plant: "#1B3327", Cultivate: "#172C22", Harvest: "#20392B" };

const dayIndex = (iso) => Math.round((toDate(iso) - toDate(START)) / 86400000);
const x = (iso) => LEFT + dayIndex(iso) * DAY_W;
const width = (start, end) => Math.max(DAY_W, (dayIndex(end) - dayIndex(start) + 1) * DAY_W);

export default function AdminHarvestTimeline() {
  const { settings, pending } = useHarvestHeader();
  const phases = settings?.phases || [];
  const pauses = settings?.pauses || [];
  const totalDays = dayIndex(END) + 1;
  const chartW = LEFT + totalDays * DAY_W + 24;
  const chartH = TOP + LANES.length * LANE_H + 34;
  const today = toIso(new Date());
  const todayInRange = dayIndex(today) >= 0 && dayIndex(today) <= dayIndex(END);

  const monthTicks = [];
  for (let i = 0; i < totalDays; i += 1) {
    const day = addDays(toDate(START), i);
    if (day.getUTCDate() === 1 || i === 0) monthTicks.push(toIso(day));
  }
  const weekTicks = [];
  for (let i = 0; i < totalDays; i += 1) {
    const day = addDays(toDate(START), i);
    if (day.getUTCDay() === 1) weekTicks.push(toIso(day));
  }

  return (
    <HarvestShell settings={settings} pending={pending}>
      <Panel title="Season timeline" testid="harvest-timeline-panel">
        <div className="overflow-x-auto">
          <svg
            width={chartW}
            height={chartH}
            role="img"
            aria-label="One Harvest Season timeline"
            data-testid="harvest-gantt"
          >
            {phases.map((phase) => (
              <g key={phase.name} data-testid={`harvest-phase-${phase.name.toLowerCase()}`}>
                <rect
                  x={x(phase.start)}
                  y={TOP - 18}
                  width={width(phase.start, phase.end)}
                  height={LANES.length * LANE_H + 24}
                  fill={PHASE_TONES[phase.name] || "#172C22"}
                  rx="6"
                />
                <text
                  x={x(phase.start) + 8}
                  y={TOP - 26}
                  fill="#C8A24A"
                  fontSize="9"
                  letterSpacing="2.4"
                  fontFamily="ui-monospace, monospace"
                >
                  {phase.name.toUpperCase()}
                </text>
              </g>
            ))}

            {pauses.map((pause) => (
              <rect
                key={pause.start}
                data-testid={`harvest-pause-${pause.start}`}
                x={x(pause.start)}
                y={TOP - 18}
                width={width(pause.start, pause.end)}
                height={LANES.length * LANE_H + 24}
                fill={pause.light ? "#F7F3E8" : "#C9C3B4"}
                fillOpacity={pause.light ? 0.08 : 0.16}
              />
            ))}

            {weekTicks.map((iso) => (
              <line
                key={`w-${iso}`}
                x1={x(iso)}
                x2={x(iso)}
                y1={TOP - 18}
                y2={TOP + LANES.length * LANE_H + 6}
                stroke="#F7F3E8"
                strokeOpacity="0.06"
              />
            ))}

            {monthTicks.map((iso) => (
              <text
                key={`m-${iso}`}
                x={x(iso) + 3}
                y={TOP - 4}
                fill="#F7F3E8"
                fillOpacity="0.45"
                fontSize="10"
                fontFamily="ui-monospace, monospace"
              >
                {toDate(iso).toLocaleDateString("en-US", { month: "short", timeZone: "UTC" })}
              </text>
            ))}

            {LANES.map((lane, i) => {
              const y = TOP + i * LANE_H + 6;
              return (
                <g key={lane.name} data-testid={`harvest-lane-${i}`}>
                  <text
                    x="0"
                    y={y + 14}
                    fill="#F7F3E8"
                    fillOpacity="0.65"
                    fontSize="11"
                    fontFamily="ui-sans-serif, system-ui"
                  >
                    {lane.name}
                  </text>
                  {(lane.bars || []).map((bar) => (
                    <g key={`${lane.name}-${bar.start}`}>
                      <rect
                        x={x(bar.start)}
                        y={y}
                        width={width(bar.start, bar.end)}
                        height={18}
                        rx="9"
                        fill={OWNER_TONES[bar.owner].fill}
                        fillOpacity="0.85"
                      >
                        <title>{`${bar.label}: ${formatLong(toDate(bar.start))} – ${formatLong(toDate(bar.end))}`}</title>
                      </rect>
                    </g>
                  ))}
                  {(lane.dots || []).map((dot) => (
                    <circle
                      key={`${lane.name}-${dot.date}`}
                      cx={x(dot.date) + DAY_W / 2}
                      cy={y + 9}
                      r="6"
                      fill={OWNER_TONES[dot.owner].fill}
                    >
                      <title>{`${dot.label}: ${formatLong(toDate(dot.date))}`}</title>
                    </circle>
                  ))}
                </g>
              );
            })}

            {todayInRange ? (
              <g data-testid="harvest-today-marker">
                <line
                  x1={x(today)}
                  x2={x(today)}
                  y1={TOP - 22}
                  y2={TOP + LANES.length * LANE_H + 10}
                  stroke="#C96E4A"
                  strokeWidth="2"
                />
                <text
                  x={x(today) + 5}
                  y={TOP + LANES.length * LANE_H + 24}
                  fill="#C96E4A"
                  fontSize="9"
                  letterSpacing="2"
                  fontFamily="ui-monospace, monospace"
                >
                  TODAY
                </text>
              </g>
            ) : null}
          </svg>
        </div>

        <div className="mt-5 flex flex-wrap gap-5" data-testid="harvest-legend">
          {Object.entries(OWNER_TONES).map(([key, tone]) => (
            <span key={key} className="flex items-center gap-2 text-xs text-cream/55">
              <span className="h-2.5 w-6 rounded-full" style={{ background: tone.fill }} />
              {tone.label}
            </span>
          ))}
          <span className="flex items-center gap-2 text-xs text-cream/55">
            <span className="h-2.5 w-6 rounded-full bg-cream/20" /> Pause / light week
          </span>
        </div>
      </Panel>

      <div className="grid gap-4 sm:grid-cols-3" data-testid="harvest-phase-cards">
        {phases.map((phase) => (
          <div
            key={phase.name}
            className="rounded-xl border border-cream/10 bg-[#10211A] px-5 py-4"
            data-testid={`harvest-phase-card-${phase.name.toLowerCase()}`}
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold/80">
              {phase.name}
            </p>
            <p className="mt-2 text-sm text-cream/80">
              {formatLong(toDate(phase.start))} – {formatLong(toDate(phase.end))}
            </p>
          </div>
        ))}
      </div>
    </HarvestShell>
  );
}
